import { parse, inject } from "regexparam";

import { useRoute } from "_/components/router";
import { Uuid } from "_/types";

const PROJECT_DETAIL_ROUTE = `/projects/:projectId`;
const MACHINE_DETAIL_ROUTE = `/machines/:machineId`;

/**
 * Routes define all the valid url paths in the application and the patterns
 * matched on. They must be valid path patterns that can be matched on by the
 * `regexparam` library.
 */
export const routes = {
  index: "/",
  authorize: "/authorize",
  project: {
    prepare: `${PROJECT_DETAIL_ROUTE}/prepare/:revisionId?`,
    optimize: `${PROJECT_DETAIL_ROUTE}/optimize/:optimizationId?/:subview?`,
    analyze: `${PROJECT_DETAIL_ROUTE}/analyze/:attemptId?/:subview?`,
    history: `${PROJECT_DETAIL_ROUTE}/history`,
    wildcards: {
      index: `${PROJECT_DETAIL_ROUTE}/*?`,
      prepare: `${PROJECT_DETAIL_ROUTE}/prepare/*?`,
      optimize: `${PROJECT_DETAIL_ROUTE}/optimize/*?`,
      analyze: `${PROJECT_DETAIL_ROUTE}/analyze/*?`,
      combinedViewer: `${PROJECT_DETAIL_ROUTE}/(prepare|analyze|optimize)/*?`,
    },
  },
  projects: {
    index: "/projects",
    wildcards: {
      index: "/projects/*?",
    },
  },
  machine: {
    index: MACHINE_DETAIL_ROUTE,
    thermals: `${MACHINE_DETAIL_ROUTE}/thermals`,
    events: `${MACHINE_DETAIL_ROUTE}/events`,
    logs: `${MACHINE_DETAIL_ROUTE}/logs`,
    configuration: `${MACHINE_DETAIL_ROUTE}/configuration`,
    wildcards: {
      index: `${MACHINE_DETAIL_ROUTE}/*?`,
    },
  },
  machines: {
    index: "/machines",
    wildcards: {
      index: "/machines/*?",
    },
  },
  jobs: "/jobs",
  settings: {
    index: "/settings",
    general: "/settings/general",
    organizations: "/settings/organizations",
    invitations: "/settings/invitations",
    organization: "/settings/organizations/:id",
    security: "/settings/security",
    wildcards: {
      index: "/settings/*?",
    },
  },
  admin: {
    index: "/admin",
    organizations: "/admin/organizations",
    organization: "/admin/organizations/:id",
    machines: "/admin/machines",
    search: "/admin/search",
    materials: "/admin/materials",
    software: "/admin/software",
    buildset: "/admin/software/:id",
    content: "/admin/content",
    wildcards: {
      index: "/admin/*?",
      organizations: "/admin/organizations/*?",
      software: "/admin/software/*?",
    },
  },
  internal: {
    index: "/internal",
    wildcards: {
      index: "/internal/*?",
    },
  },
  public: {
    headless: "/_headless_",
    login: "/login",
    bootstrap: "/bootstrap",
    verify: "/verify",
    recovery: "/recovery",
    rsvp: "/rsvp",
  },

  docs: {
    index: "/docs",
    page: "/docs/:slug",
    wildcards: {
      // A manually defined regex is required to match on slugs containing
      // "/" characters, as these are not matched by the default route parser.
      index: /^\/docs(?:\/(?<slug>[^/].*)?)?/,
    },
  },
} as const;

export const adminRoutes = routes.admin;
export const docsRoutes = routes.docs;
export const machineRoutes = routes.machine;
export const projectRoutes = routes.project;
export const publicRoutes = routes.public;
export const settingsRoutes = routes.settings;

/**
 * Route URLs are the functions and static URLs that should be used when constructing links
 * and navigating within the application. In essence, these are the concrete forms of routes.
 */
export const routeUrls = {
  index: routes.index,
  authorize: routes.authorize,
  project: {
    index: (projectId: Uuid) => inject(routes.project.prepare, { projectId }),
    prepare: (projectId: Uuid, revisionId?: Uuid) =>
      inject(routes.project.prepare, { projectId, revisionId }),
    optimize: (projectId: Uuid, optimizationId?: Uuid) =>
      inject(routes.project.optimize as string, { projectId, optimizationId }),
    optimizeThermal: (projectId: Uuid, optimizationId: Uuid) =>
      inject(routes.project.optimize as string, {
        projectId,
        optimizationId,
        subview: "thermal",
      }),
    analyze: (projectId: Uuid, attemptId?: Uuid) =>
      inject(routes.project.analyze as string, { projectId, attemptId }),
    analyzeTelemetry: (projectId: Uuid, attemptId: Uuid) =>
      inject(routes.project.analyze as string, {
        projectId,
        attemptId,
        subview: "telemetry",
      }),
    history: (projectId: Uuid) => inject(routes.project.history, { projectId }),
  },
  projects: {
    index: routes.projects.index,
  },
  machine: {
    index: (machineId: Uuid) => inject(routes.machine.index, { machineId }),
    thermals: (machineId: Uuid) =>
      inject(routes.machine.thermals, { machineId }),
    events: (machineId: Uuid) => inject(routes.machine.events, { machineId }),
    logs: (machineId: Uuid) => inject(routes.machine.logs, { machineId }),
    configuration: (machineId: Uuid) =>
      inject(routes.machine.configuration, { machineId }),
  },
  machines: {
    index: routes.machines.index,
  },
  jobs: routes.jobs,
  settings: {
    index: routes.settings.index,
    general: routes.settings.general,
    security: routes.settings.security,
    organizations: routes.settings.organizations,
    invitations: routes.settings.invitations,
    organization: (id: Uuid) => inject(routes.settings.organization, { id }),
  },
  admin: {
    index: routes.admin.index,
    organizations: routes.admin.organizations,
    organization: (id: Uuid) => inject(routes.admin.organization, { id }),
    machines: routes.admin.machines,
    materials: routes.admin.materials,
    software: routes.admin.software,
    search: routes.admin.search,
    content: routes.admin.content,
    buildset: (id: Uuid) => inject(routes.admin.buildset, { id }),
  },
  docs: {
    index: routes.docs.index,
    page: (slug: string) => inject(routes.docs.page, { slug }),
  },
  public: routes.public,
};

export const projectUrls = routeUrls.project;
export const machineUrls = routeUrls.machine;
export const settingsUrls = routeUrls.settings;
export const adminUrls = routeUrls.admin;
export const publicUrls = routeUrls.public;
export const docsUrls = routeUrls.docs;

export function useOptimizeRoute():
  | { subview: "main" | "thermal"; optimizationId: string | undefined }
  | undefined {
  const [match, params] = useRoute<{
    optimizationId?: string;
    subview?: string;
  }>(projectRoutes.optimize);
  if (!match) return undefined;
  return {
    subview: params?.subview === "thermal" ? "thermal" : "main",
    optimizationId: params?.optimizationId,
  };
}

export function useAnalyzeRoute():
  | { subview: "viewer" | "telemetry"; attemptId: string | undefined }
  | undefined {
  const [match, params] = useRoute<{
    attemptId?: string;
    subview?: string;
  }>(projectRoutes.analyze);
  if (!match) return undefined;
  return {
    subview: params?.subview === "telemetry" ? "telemetry" : "viewer",
    attemptId: params?.attemptId,
  };
}

export const isPublicUrl = (pathname: string) => {
  return Object.values(publicUrls).some((route) =>
    parse(route).pattern.test(pathname)
  );
};

export const matchesAnyRoute = (
  pathname: string,
  routePatterns: Array<string | RegExp>
): boolean =>
  routePatterns.some((route) => {
    // Routes may be strings which need parsing, or direct regex patterns.
    const pattern = typeof route === "string" ? parse(route).pattern : route;
    return pattern.test(pathname);
  });

// Checks whether the given pathname is a URL which requires an active org to
// be set for the user. Additional routes which do not require an org to be set
// should be added to the EXCEPTIONS list.
export const isOrgUrl = (pathname: string): boolean => {
  // Assume all URLs are org urls unless they are in the exceptions list.
  const EXCEPTIONS: Array<string | RegExp> = [
    /^mailto:(.*)@.*/,
    routes.settings.wildcards.index,
    routes.docs.wildcards.index,
  ];

  return !matchesAnyRoute(pathname, EXCEPTIONS);
};
